/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import React, { useCallback, useMemo, useRef, CSSProperties } from 'react';
import {
  Autocomplete,
  autocompleteClasses,
  InputAdornment,
  Popper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import { improvedFilterOptions } from 'util/shared/common';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SymOption {
  symbol: string;
  name?: string;
}

interface CanvasSymSelectorProps {
  sym: string;
  options: SymOption[];
  onSymChange: (sym: string) => void;
  textFieldStyleOverrides?: CSSProperties;
}

// ── Virtualisation helpers (react-window) ─────────────────────────────────────

const LISTBOX_PADDING = 6;

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: number) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

function RenderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  return (
    <Typography component="li" {...dataSet.props} noWrap style={inlineStyle}>
      {dataSet.option}
    </Typography>
  );
}

const VirtualizedListbox = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function VirtualizedListbox(props, ref) {
  const { children, ...other } = props;
  const itemData: Array<{ props: any; option: any }> = [];

  React.Children.forEach(children as React.ReactElement[], (child) => {
    if (child) {
      itemData.push({ props: child.props, option: child.props.children });
    }
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const itemCount = itemData.length;
  const itemSize = smUp ? 40 : 48;
  const height = Math.min(itemCount, 10) * itemSize + 2 * LISTBOX_PADDING;

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={height}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={() => itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {RenderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

// ── Styled Popper (matched to input width) ────────────────────────────────────

const SELECTOR_WIDTH = 220;

const StyledPopper = styled(Popper)(({ theme }) => ({
  width: `${SELECTOR_WIDTH}px !important`,
  zIndex: theme.zIndex.modal + 1,
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    padding: 0,
    '& ul': { padding: 0, margin: 0 },
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.35)}`,
    borderRadius: 6,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  [`& .${autocompleteClasses.option}`]: {
    padding: '4px 10px',
    minHeight: 'auto',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },
  [`& .${autocompleteClasses.noOptions}`]: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    padding: '10px 12px',
  },
}));

// ── Component ─────────────────────────────────────────────────────────────────

const CanvasSymSelector = ({
  sym,
  options,
  onSymChange,
  textFieldStyleOverrides,
}: CanvasSymSelectorProps) => {
  const theme = useTheme();

  const optionMap = useMemo(
    () => new Map(options.map((o) => [o.symbol, o])),
    [options],
  );

  const symbolList = useMemo(() => options.map((o) => o.symbol), [options]);

  const inputRef = useRef<HTMLInputElement>(null);

  const optionSet = useMemo(() => new Set(symbolList), [symbolList]);

  const handleChange = useCallback(
    (_e: React.SyntheticEvent, value: string | null) => {
      if (value != null && optionSet.has(value)) {
        onSymChange(value);
        inputRef.current?.blur();
      }
    },
    [onSymChange, optionSet],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        const raw = (e.target as HTMLInputElement).value?.trim().toUpperCase();
        if (raw && optionSet.has(raw)) {
          e.preventDefault();
          onSymChange(raw);
          inputRef.current?.blur();
        }
      }
    },
    [onSymChange, optionSet],
  );

  return (
    <Autocomplete
      className="canvas-no-drag"
      size="small"
      freeSolo
      value={sym}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      options={symbolList}
      filterOptions={improvedFilterOptions}
      disableClearable
      openOnFocus
      selectOnFocus
      autoHighlight
      handleHomeEndKeys
      noOptionsText="No matching symbols"
      PopperComponent={StyledPopper}
      ListboxComponent={VirtualizedListbox}
      renderOption={(props, option) => {
        const detail = optionMap.get(option);
        return (
          <li {...props} key={option}>
            <Stack sx={{ width: '100%', overflow: 'hidden', py: 0.25 }}>
              <Typography
                fontSize={12}
                fontWeight={700}
                sx={{ letterSpacing: '0.02em' }}
              >
                {option}
              </Typography>
              {detail?.name && detail.name !== option && (
                <Typography
                  fontSize={10}
                  color="text.secondary"
                  noWrap
                  sx={{ opacity: 0.6, lineHeight: 1.3 }}
                >
                  {detail.name}
                </Typography>
              )}
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder={`Search (${sym})`}
          sx={{
            width: SELECTOR_WIDTH,
            '& .MuiOutlinedInput-root': {
              height: 30,
              fontSize: 12,
              fontWeight: 600,
              borderRadius: '6px',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              transition: 'all 0.15s ease',
              '& fieldset': { border: 'none' },
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.default, 0.8),
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              '&.Mui-focused': {
                backgroundColor: theme.palette.background.paper,
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
              },
            },
            '& .MuiInputBase-input': {
              padding: '2px 4px 2px 0 !important',
              '&::placeholder': {
                opacity: 0.6,
                fontSize: 12,
              },
            },
            ...(textFieldStyleOverrides != null ? textFieldStyleOverrides : {}),
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.25, mr: 0 }}>
                <SearchIcon
                  sx={{
                    fontSize: 15,
                    color: alpha(theme.palette.text.secondary, 0.4),
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

export default CanvasSymSelector;
export type { CanvasSymSelectorProps };
